import { ClientResponse } from "@/Common/Api/ClientResponse";
import { FlowsheetClient } from "@/Flowsheets/Api/FlowsheetClient";
import { Flowsheet } from "@/Flowsheets/Models/Flowsheet";
import { ICareComponent, ICareElement } from "../Api/Contracts/GetFlowsheet";
import { FlowsheetConstants } from "../FlowsheetConstants";
import { CareElement } from "../Models/CareElements/CareElement";
import { CareElementType } from "../Models/CareElements/CareElementType";
import { RiskFactorCareElement } from "../Models/CareElements/RiskFactorCareElement";
import { useCommand, UseCommandOptions, UseCommandResult } from "./useCommand";

interface UseSaveFlowsheetResult extends UseCommandResult<ClientResponse, unknown, Flowsheet> {
    saveFlowsheet: (flowsheet: Flowsheet) => Promise<void>;
}

interface UseSaveFlowsheetOptions extends UseCommandOptions<ClientResponse<void>, Error> {
}

function useSaveFlowsheet(useSaveFlowsheetOptions?: UseSaveFlowsheetOptions): UseSaveFlowsheetResult {
    async function command(flowsheet: Flowsheet): Promise<ClientResponse> {
        const editableCareComponents = flowsheet.CareComponents.filter(c => c.Name !== "MedicationCareComponent");

        const response = await FlowsheetClient.saveFlowsheet({ FlowsheetId: flowsheet.Id, CareComponents: processCareComponents(editableCareComponents) });

        response.throwIfError();

        return response;
    }

    const mutation = useCommand(command, useSaveFlowsheetOptions);

    return {
        ...mutation,
        saveFlowsheet: mutation.mutate
    };
}

function processCareComponents(careComponents: ICareComponent[]): ICareComponent[] {
    return careComponents?.map(careComponent => {
        if (careComponent?.Name === FlowsheetConstants.RiskFactors.CareComponentName) {
            const riskFactorCareElements = careComponent.CareElements as RiskFactorCareElement[];

            riskFactorCareElements?.forEach(careElement => {
                if (!careElement.ReportingPeriod?.StartDate) {
                    careElement.ReportingPeriod = null;
                }
            });
        }
        else {
            careComponent.CareElements = careComponent.CareElements.map(mapCareElement);
        }

        return careComponent;
    });
}

function mapCareElement(careElement: CareElement): ICareElement {
    switch (careElement.CareElementType) {
        case CareElementType.IntegerReviewed:
        case CareElementType.BooleanReviewed:
        case CareElementType.BooleanTextReviewed:
        case CareElementType.TextReviewed:
        case CareElementType.DecimalReviewed:
        case CareElementType.DateOnlyReviewed:
        case CareElementType.MultiSelectReviewed:
        case CareElementType.Subject:
        case CareElementType.FirstLineReviewed:
        case CareElementType.AgentDoseReviewed:
            return {
                ...careElement,
                DateReviewed: careElement.DateReviewedWithPrecision?.value
            };
        default:
            return careElement;
    }
}

export {
    useSaveFlowsheet
};
